<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between bg-brand-600 text-white p-4 rounded-t-lg">
      <div>
        <p class="text-xs font-medium leading-4 tracking-normal">Payment Plan Amendment</p>
        <p class="text-base font-semibold leading-6 tracking-normal">{{ order?.reference }}</p>
      </div>
      <div class="flex flex-col items-start">
        <p class="text-xs">Powered by</p>
        <img src="@/assets/white_layup_logo.svg" alt="LayUp Logo" class="mt-1 h-[26px] w-[72px]" />
      </div>
    </div>

    <!-- Order Details -->
    <section>
      <h2 class="text-lg font-semibold text-grey-light-900 mb-2">Order Details</h2>
      <div class="space-y-1 text-sm text-grey-light-700">
        <div class="flex justify-between">
          <span>Name</span><span class="font-semibold">{{ customerName }}</span>
        </div>
        <div class="flex justify-between">
          <span>Order Name</span><span class="font-semibold">{{ order?.name }}</span>
        </div>
        <div class="flex justify-between">
          <span>Order Reference</span><span class="font-semibold">{{ order?.reference }}</span>
        </div>
        <div class="flex justify-between">
          <span>Merchant</span><span class="font-semibold">{{ order?.merchantName }}</span>
        </div>
      </div>
    </section>

    <!-- Amended Plan -->
    <section>
      <h2 class="text-lg font-semibold text-grey-light-900 mb-2">Amended Plan</h2>
      <div class="space-y-1 text-sm text-grey-light-700">
        <div class="flex justify-between">
          <span>Original total</span><span class="font-semibold">{{ displayCurrency(originalOrderTotal) }}</span>
        </div>
        <div class="flex justify-between">
          <span>Amended total</span
          ><span class="font-semibold text-brand-700">{{ displayCurrency(amendedTotal) }}</span>
        </div>
        <div class="flex justify-between">
          <span>Amount paid</span><span class="font-semibold">{{ displayCurrency(totalPaid) }}</span>
        </div>
        <div class="flex justify-between">
          <span>Balance remaining</span><span class="font-semibold">{{ displayCurrency(balanceDue) }}</span>
        </div>
      </div>
    </section>

    <!-- Key Dates -->
    <section>
      <h2 class="text-lg font-semibold text-grey-light-900 mb-2">Key Dates</h2>
      <div class="space-y-3 text-sm text-grey-light-700">
        <div v-if="newTravelDate" class="flex items-center justify-between">
          <span>New Travel Date</span>
          <span class="font-semibold text-grey-light-900">{{ displayDate(newTravelDate) }}</span>
        </div>

        <div v-if="newFinalPaymentDate" class="rounded-lg border border-brand-200 bg-brand-50 p-3">
          <div class="flex items-center gap-2 text-brand-700">
            <span>New Final Payment Date</span>
          </div>
          <div class="mt-1 font-semibold text-brand-700">{{ displayDate(newFinalPaymentDate) }}</div>
        </div>
      </div>
    </section>

    <!-- Payment Schedule -->
    <div class="space-y-2">
      <div class="text-xl font-semibold text-grey-light-900">Payment Schedule</div>

      <div v-if="!visibleInstallments.length" class="text-sm text-grey-light-500">No upcoming installments.</div>

      <div
        v-for="payment in visibleInstallments"
        :key="payment.key"
        class="flex items-center justify-between text-grey-light-700"
      >
        <span>{{ payment.monthLabel }}</span>
        <span class="font-semibold text-grey-light-900">{{ displayCurrency(payment.amount) }}</span>
      </div>

      <button
        v-if="upcomingInstallments.length > defaultVisibleInstallments"
        type="button"
        class="flex w-full items-center justify-between text-left text-sm font-medium text-grey-light-700"
        @click="showAllInstallments = !showAllInstallments"
      >
        <span>{{ showAllInstallments ? 'View less' : 'View all' }}</span>
        <ChevronDown class="transition-all" :class="{ 'rotate-180': showAllInstallments }" />
      </button>

      <div class="flex items-center justify-between border-t border-grey-light-200 pt-2 text-grey-light-700">
        <span class="font-semibold">Total remaining</span>
        <span class="font-semibold text-grey-light-900">{{ displayCurrency(totalRemaining) }}</span>
      </div>
    </div>

    <!-- Footer -->
    <div class="flex flex-col gap-3 pt-6">
      <div
        v-if="acceptMessage"
        class="rounded-lg border p-3 text-sm font-medium"
        :class="
          acceptStatus === 'success'
            ? 'border-green-200 bg-green-50 text-green-700'
            : 'border-red-200 bg-red-50 text-red-700'
        "
      >
        {{ acceptMessage }}
      </div>
      <div class="flex items-start justify-between pt-4 text-xs text-grey-light-500">
        <div class="flex flex-col items-start">
          <p class="text-xs text-grey-light-500">Powered by</p>
          <img src="@/assets/logo.svg" alt="LayUp Logo" class="mt-1 h-[26px] w-[72px]" />
        </div>
        <span class="pt-1">Generated {{ generatedDateLabel }}</span>
      </div>
      <div class="flex gap-2">
        <Button class="flex-1 h-10 text-white hover:bg-brand-800 rounded-md" @click="onDownloadPdf">
          Download PDF
        </Button>
        <Button class="flex-1 h-10 text-white hover:bg-brand-800 rounded-md" @click="onShareLink"> Share link </Button>
      </div>
      <div class="flex gap-2">
        <Button variant="outline" size="icon" class="shrink-0" @click="emit('back')">
          <ChevronLeft class="h-4 w-4" />
        </Button>
        <Button
          class="flex-1 h-10 text-white hover:bg-brand-800 rounded-md"
          :disabled="!isAdmin"
          @click="onConfirmChanges"
        >
          {{ confirmButtonLabel }}
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { acceptAmendmentPreview, getAmendmentPreviewPdf, getAmendmentPreviewPdfShareLink } from '@/services/amendment';
import { useMerchantStore } from '@/stores/merchants';
import { useUserStore } from '@/stores/users';
import { displayCurrency, displayDate } from '@/utils/format.utils';
import { createException, createWarning } from '@/utils/monitoring.utils';
import type { pdfData } from '@/utils/pdfutils';
import dayjs from 'dayjs';
import { ChevronDown, ChevronLeft } from 'lucide-vue-next';
import { computed, onMounted, ref } from 'vue';
import type { ModelOrderAmendmentPreviewResponse, ModelOrderResponse } from '@layup/api-sdk';

const props = defineProps<{
  preview: ModelOrderAmendmentPreviewResponse | null;
  order?: ModelOrderResponse;
  shouldAcceptOnConfirm?: boolean;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'confirm'): void;
}>();

const userStore = useUserStore();
const merchantStore = useMerchantStore();
const customerName = computed(() => props?.order?.plans?.[0]?.benefactor?.name || '--');
const showAllInstallments = ref(false);
const defaultVisibleInstallments = 2;
const isAccepting = ref(false);
const acceptStatus = ref<'success' | 'error' | null>(null);
const acceptMessage = ref('');
const originalOrderTotal = ref(0);
const totalPaid = computed(() => {
  const plans = props?.order?.plans ?? [];
  return plans.reduce((sum, plan: any) => {
    const paidTotal = (plan.payments ?? []).reduce((paymentSum: number, payment: any) => {
      return paymentSum + (payment.paid ? payment.amount || 0 : 0);
    }, 0);
    return sum + paidTotal;
  }, 0);
});
const isAdmin = computed(() => userStore.user?.merchantRole === 'ADMIN');
const amendedTotal = computed(() => {
  const products = props.preview?.products ?? [];
  if (products.length) {
    return products.reduce((sum, product) => sum + (product.amount ?? 0), 0);
  }
  return props.preview?.amendedPlan?.reduce((sum, plan) => sum + (plan.amountDue ?? 0), 0) ?? originalOrderTotal.value;
});

const balanceDue = computed(() => {
  const remaining = amendedTotal.value - totalPaid.value;
  return remaining > 0 ? remaining : 0;
});

onMounted(async () => {
  originalOrderTotal.value = Number(props?.order?.amountDue || 0);
});

const newTravelDate = computed(() => props.preview?.travelDate || props.preview?.suggestedTravelDate || '');
const newFinalPaymentDate = computed(() => props.preview?.endDate || '');

const upcomingInstallments = computed(() => {
  const plans = props.preview?.amendedPlan ?? [];
  const planWithPayments = plans.find((plan) => (plan.payments?.length ?? 0) > 0);
  const payments = planWithPayments?.payments ?? [];

  return payments
    .filter(
      (payment) =>
        !payment.paid &&
        payment.pending !== true &&
        (payment as any).locked !== true &&
        payment.failed !== true &&
        payment.paymentType !== 'REFUND',
    )
    .sort((a, b) => dayjs(a.due).valueOf() - dayjs(b.due).valueOf())
    .map((payment, index) => ({
      key: `${payment._id || payment.due || index}`,
      monthLabel: payment.due ? dayjs(payment.due).format('MMMM') : `Installment ${index + 1}`,
      amount: payment.amount ?? 0,
    }));
});

const visibleInstallments = computed(() => {
  if (showAllInstallments.value) return upcomingInstallments.value;
  return upcomingInstallments.value.slice(0, defaultVisibleInstallments);
});

const totalRemaining = computed(() => {
  return upcomingInstallments.value.reduce((sum, payment) => sum + payment.amount, 0);
});

const generatedDateLabel = computed(() => {
  if (!props.preview?.updatedAt) return '--';
  return dayjs(props.preview.updatedAt).format('DD MMM YYYY');
});

const confirmButtonLabel = computed(() => {
  if (isAccepting.value) return 'Confirming...';
  if (acceptStatus.value === 'success') return 'Confirmed';
  return 'Confirm changes';
});

const buildPdfData = (): pdfData | null => {
  if (!props.preview || !props.order) return null;

  return {
    orderReference: props.order.reference ?? '',
    orderName: props.order.name ?? '',
    previewDate: displayDate(props.preview.updatedAt),
    customerName: customerName.value,
    merchantName: props.order.merchantName ?? '',
    originalTotal: originalOrderTotal.value,
    amendedTotal: amendedTotal.value,
    totalPaid: totalPaid.value,
    balanceDue: balanceDue.value,
    travelDate: newTravelDate.value,
    endDate: newFinalPaymentDate.value,
    installments: upcomingInstallments.value.map((p) => ({
      month: p.monthLabel,
      amount: p.amount,
    })),
    totalRemaining: balanceDue.value,
    daysBeforeTravel: merchantStore.merchant.features?.numberOfDayAfterEndDateToTravel,
  };
};

const onDownloadPdf = async () => {
  const pdfData = buildPdfData();
  if (!pdfData || !props.order) return;
  try {
    // Get raw ArrayBuffer from backend
    const arrayBuffer = await getAmendmentPreviewPdf(pdfData);

    // Wrap in Blob
    const blob = new Blob([arrayBuffer], { type: 'application/pdf' });

    // Trigger download
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `amendment-${props.order.reference}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    createException(error as Error);
    alert('Failed to generate PDF');
  }
};

const onShareLink = async () => {
  const pdfData = buildPdfData();
  if (!pdfData) return;

  try {
    const response = await getAmendmentPreviewPdfShareLink(pdfData);
    const linkToCopy = response.shareUrl || response.viewUrl;

    try {
      await navigator.clipboard.writeText(linkToCopy);
      alert('Amendment PDF link copied to clipboard!');
    } catch (clipboardError) {
      createWarning(clipboardError, {
        message: 'Clipboard failed while copying amendment PDF link',
        linkPresent: Boolean(linkToCopy),
      });

      // Fallback
      window.prompt('Copy this link manually:', linkToCopy);
    }
  } catch (apiError) {
    createWarning(apiError, {
      message: 'Failed to fetch amendment PDF share link',
    });
    alert('Failed to generate link. Please try again.');
  }
};

const onConfirmChanges = () => {
  if (!props.preview?._id || isAccepting.value) {
    acceptStatus.value = 'error';
    acceptMessage.value = 'Unable to accept amendment: missing preview ID.';
    return;
  }

  isAccepting.value = true;
  acceptStatus.value = null;
  acceptMessage.value = '';

  acceptAmendmentPreview(props.preview._id)
    .then(() => {
      acceptStatus.value = 'success';
      acceptMessage.value = 'Amendment saved successfully.';
      emit('confirm');
    })
    .catch((error) => {
      createException(error as Error);
      acceptStatus.value = 'error';
      acceptMessage.value = 'Failed to accept amendment. Please try again.';
    })
    .finally(() => {
      isAccepting.value = false;
    });
};
</script>

<style scoped>
.bg-brand-700 {
  background-color: #1fafb3;
}
.text-brand-700 {
  color: #1fafb3;
}
</style>
